import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Card, Row } from 'react-bootstrap';
import { get, capitalize, isEqual } from 'lodash';
import classNames from 'classnames';
import { FormikProps, useFormikContext } from 'formik';
import AlertCircleOutline from 'mdi-react/AlertCircleOutlineIcon';

import { scanerSelector } from 'modules/common/ScanerBtn/selectors';

import { PhoneService } from '../../../entities/Phone/service';
import { Image } from '../../../entities/Photo/interfaces';
import { Document } from '../../../entities/Document';
import { RolesMode } from '../../../entities/Roles';
import { TripStatusIds, tripDetailsType, Trip } from '../../../entities/Trip';

import PhotoGrid from './components/PhotoGrid';
import DriverInfoGrid from './components/DriverInfoGrid';
import TransportInfoGrid from './components/TransportInfoGrid';
import ScanerModal from './components/ScanerModal';

import styles from './FlightDetails.module.scss';

export type LabelMark = 'fastLine' | 'block' | 'declaimed' | 'ownCar';

const TITLES = {
  fastLine: 'Fast line',
  block: 'В чорному списку',
  declaimed: 'Відкладено',
  ownCar: 'Автомобіль СМЦ',
};

type ImgDataType = {
  serverImages: any[];
  images: any[];
};

type OptionalReq = {
  isTransportReq: boolean;
  isForwarderReq: boolean;
};

type Props = {
  mode: RolesMode;
  tripType: tripDetailsType;
  statusId: TripStatusIds;
  handleChange: (e: React.ChangeEvent<any>) => void;
  onFaceID?: (type: string, values: any) => void;
  isBlockFace?: any;
  operatingProcedure: string;
};

const FlightDetails: React.FC<Props> = ({
  mode,
  tripType,
  handleChange,
  onFaceID,
  isBlockFace,
  statusId,
  operatingProcedure,
}) => {
  const formik: FormikProps<Trip> = useFormikContext();
  const { typeDoc } = useSelector(scanerSelector);

  const { values, errors, setFieldValue } = formik;
  const [focused, setFocused] = useState<string>('');

  const isDriver = tripType === 'driver';
  const isTransport = tripType === 'vehicle';
  const isForwarder = tripType === 'forwarder';
  let title = 'Експедитор';

  if (isDriver) title = 'Водій';
  if (isTransport) title = 'Транспорт';

  const documentVehicle = get(values, `${tripType}.document`, { number: '', pictureFiles: [], pictures: [], type: 0 });
  const documentTrailer = get(values, 'trailer.document', { number: '', pictureFiles: [], pictures: [], type: 0 });
  const documents = get(values, `${tripType}.documents`, []);
  const vehicleImagesPF = get(values, 'vehicle.document.pictureFiles', []);
  const trailerImagesPF = get(values, 'trailer.document.pictureFiles', []);
  const isFaceId = get(values, 'driver.isFaceId', false);
  const isFaceIdForwarder = get(values, 'forwarder.isFaceId', false);
  const fastLine = get(values, `isFastLine${capitalize(tripType)}`, 'fastLine');
  const driverBlackList = get(values, `driver.isBlackList`, false);
  const forwarderBlackList = get(values, `forwarder.isBlackList`, false);
  const carBlackList = get(values, `vehicle.isBlackList`, false);
  const trailerBlackList = get(values, `trailer.isBlackList`, false);
  const ownCar = get(values, `vehicle.isSMCTransport`, false);
  const ownTrailer = get(values, `trailer.isSMCTransport`, false);

  const labels: LabelMark[] = [fastLine];

  const imgData = useMemo<ImgDataType>(() => {
    const data = {
      serverImages: [] as Image[],
      images: [] as any[],
    };

    if (isTransport) {
      [documentVehicle, documentTrailer].forEach(({ number, pictures, pictureFiles = [] }: any, index: number) => {
        data.serverImages.push(
          ...pictures.map((v: Image, indx: number) => ({
            ...v,
            type: !index ? 'vehicle' : 'trailer',
            docIndex: index,
            picIndex: indx,
            docNumber: number,
          })),
        );
        data.images.push(
          ...pictureFiles.map((file: File, indx: number) => ({
            file,
            type: !index ? 'vehicle' : 'trailer',
            docIndex: index,
            picIndex: indx,
            docNumber: number,
          })),
        );
      });
      return data;
    }
    // docIndex: index => паспорт: 0, удостоверение: 1
    documents?.forEach(({ number, pictures, type, pictureFiles = [] }: Document, index: number) => {
      // type 1 - паспорт | type 3 - удостоверение
      if (!isDriver && isEqual(type, 1)) return;

      data.serverImages.push(
        ...pictures.map((v: Image, indx: number) => ({
          ...v,
          type: isDriver ? 'driver' : 'forwarder',
          docIndex: index,
          docType: type,
          picIndex: indx,
          docNumber: number,
        })),
      );

      data.images.push(
        ...pictureFiles.map((file: File, indx: number) => ({
          file,
          type: isDriver ? 'driver' : 'forwarder',
          docIndex: index,
          docType: type,
          picIndex: indx,
          docNumber: number,
        })),
      );
    });

    return data;
  }, [documents, documentVehicle, documentTrailer]);

  const optReqMark = useMemo<OptionalReq>(() => {
    const phone = PhoneService.unformatPhone(get(values, 'forwarder.phone.number', ''));

    return {
      isTransportReq: !!get(values, 'trailer.document.number', null) || !!get(values, 'trailer.licensePlate', null),
      isForwarderReq:
        phone.length > 3 ||
        !!get(values, 'forwarder.lastName', null) ||
        !!get(values, 'forwarder.firstName', null) ||
        !!get(values, 'forwarder.middleName', null) ||
        !!get(values, 'forwarder.documents[0].number', null),
    };
  }, [values]);

  const cardVerify = {
    driver: values.isDriverDocumentValid,
    forwarder: values.isForwarderDocumentValid,
    vehicle: values.isTransportDocumentValid,
  } as {
    [key: string]: boolean;
  };

  const faceIdErr = (isDriver || isForwarder) && get(values, `${tripType}.firstName`);

  const isErrors =
    (errors && errors[tripType]) ||
    (errors && tripType === 'vehicle' && errors.trailer) ||
    (errors && tripType === 'vehicle' && errors.entryVehicleWeight) ||
    // @ts-ignore
    (errors && errors[`is${capitalize(tripType)}DocumentValid`]) ||
    (errors && tripType === 'vehicle' && errors.isTransportDocumentValid);
  // eslint-disable-next-line no-console
  // console.log(`isErrors: ${isErrors ? JSON.stringify(isErrors) : isErrors}; tripType: ${tripType}; errors: `, errors);

  const isPhotoError =
    // eslint-disable-next-line
    isErrors && isErrors.documents && isErrors.documents.length
      ? isErrors.documents.find((d: any) => d && d.pictureFiles)
      : isErrors && isErrors.document
      ? !!isErrors.document.pictureFiles
      : false;

  return (
    <Card className="mb-3">
      <Card.Header className="d-flex align-items-center py-0">
        {isErrors && (
          <span className="d-flex align-items-center justify-content-center error-circle">
            <AlertCircleOutline size={15} className="text-danger" />
          </span>
        )}
        <span className="d-block font-weight-bolder ml-4 mr-5 py-1">{title}</span>
        {labels.map((label, index) => (
          <span
            key={index}
            className={classNames('d-flex justify-content-center font-weight-bolder text-white', styles[label])}
          >
            {TITLES[label]}
          </span>
        ))}
        {driverBlackList && isDriver ? (
          <span className={classNames('d-flex justify-content-center font-weight-bolder text-white', styles.block)}>
            Водій в чорному списку
          </span>
        ) : null}
        {forwarderBlackList && isForwarder ? (
          <span className={classNames('d-flex justify-content-center font-weight-bolder text-white', styles.block)}>
            Експедитор в чорному списку
          </span>
        ) : null}
        {ownTrailer && isTransport ? (
          <span className={classNames('d-flex justify-content-center font-weight-bolder text-white', styles.ownCar)}>
            Причеп СМЦ
          </span>
        ) : null}
        {ownCar && isTransport ? (
          <span className={classNames('d-flex justify-content-center font-weight-bolder text-white', styles.ownCar)}>
            Автомобіль СМЦ
          </span>
        ) : null}
        {carBlackList && isTransport ? (
          <span className={classNames('d-flex justify-content-center font-weight-bolder text-white', styles.block)}>
            Автомобіль в чорному списку
          </span>
        ) : null}
        {trailerBlackList && isTransport ? (
          <span className={classNames('d-flex justify-content-center font-weight-bolder text-white', styles.block)}>
            Причеп в чорному списку
          </span>
        ) : null}
        {mode === 'ordering' && isDriver && faceIdErr && faceIdErr.length && isBlockFace() && !isFaceId ? (
          <span
            className={classNames('d-flex justify-content-center font-weight-bolder text-white', styles.customTooltip)}
          >
            Додайте FaceID
          </span>
        ) : null}
        {mode === 'ordering' && isForwarder && faceIdErr && faceIdErr.length && isBlockFace() && !isFaceIdForwarder ? (
          <span
            className={classNames('d-flex justify-content-center font-weight-bolder text-white', styles.customTooltip)}
          >
            Додайте FaceID
          </span>
        ) : null}
      </Card.Header>
      <Card.Body>
        <Row>
          {(isDriver || isForwarder) && (
            <DriverInfoGrid
              mode={mode}
              statusId={statusId}
              type={tripType}
              isVerified={cardVerify[tripType]}
              documents={documents}
              isOptRequired={isDriver ? false : optReqMark.isForwarderReq}
              {...(onFaceID && { onFaceID: () => onFaceID(tripType, values) })}
              handleChange={handleChange}
              setFocus={setFocused}
              operatingProcedure={operatingProcedure}
            />
          )}
          {isTransport && (
            <TransportInfoGrid
              mode={mode}
              statusId={statusId}
              handleChange={handleChange}
              isVerified={cardVerify[tripType]}
              setFieldValue={setFieldValue}
              vehicleImages={vehicleImagesPF}
              trailerImages={trailerImagesPF}
              isOptRequired={optReqMark.isTransportReq}
              setFocus={setFocused}
              operatingProcedure={operatingProcedure}
            />
          )}
          <PhotoGrid
            typeCard={tripType}
            mode={mode}
            statusId={statusId}
            serverImages={imgData.serverImages}
            images={imgData.images}
            setFieldValue={setFieldValue}
            values={values}
            isPhotoError={!!isPhotoError}
            activeIndex={focused}
          />
        </Row>
      </Card.Body>
      {(typeDoc === tripType || (typeDoc === 'trailer' && tripType === 'vehicle')) && typeDoc && <ScanerModal />}
    </Card>
  );
};

export default FlightDetails;
